/**
 * Web Push (FCM) client for "a client emailed you" notifications.
 *
 * The owner triages mail on a phone, so the app must be able to ping a
 * locked device. The moving parts:
 *
 *   server  notifyOnNewEmail (Firestore trigger) sends a DATA-ONLY FCM message
 *   sw      public/firebase-messaging-sw.js shows the notification (data-only
 *           is required for iOS, which demands the SW call showNotification)
 *   here    permission + FCM token + (un)registration with our own endpoints
 *
 * Everything here is pure-function-first so the decision logic (is push even
 * possible on this device? is the opt-in banner snoozed? is this a sane email
 * id?) is unit-testable without a browser. The impure entry points
 * (enablePush / disablePush / syncPushTokenIfGranted) NEVER throw: every
 * failure comes back as a typed PushResult the UI can render.
 *
 * Sign-out is handled centrally (see ensureSignOutCleanup): a Firebase Auth
 * beforeAuthStateChanged hook unregisters this device while the ID token is
 * still valid, so none of the many sign-out call sites has to remember to.
 *
 * Never log an FCM token here (not even in part) - failures log a reason code.
 */

import type { User } from 'firebase/auth';
import { app, auth } from '../../firebase';
import { authPostConfirmed } from './cloudFunctionsAuth';

// --- Endpoints / constants --------------------------------------------------

const CF = 'https://us-central1-web-construct-app.cloudfunctions.net';
export const REGISTER_PUSH_TOKEN_URL = `${CF}/registerPushToken`;
export const UNREGISTER_PUSH_TOKEN_URL = `${CF}/unregisterPushToken`;

/** Origin-relative on purpose: the app is served from a custom domain AND
 *  *.web.app, and a SW must live on the origin it controls. */
export const PUSH_SW_URL = '/firebase-messaging-sw.js';

/** Query param a notification click lands on: '/?openEmail=<emailDocId>'. */
export const OPEN_EMAIL_PARAM = 'openEmail';

/** Optional Web Push certificate key (Firebase console > Cloud Messaging).
 *  The SDK falls back to its default key when this is unset. */
const VAPID_KEY: string | undefined = process.env.REACT_APP_FCM_VAPID_KEY || undefined;

const OPT_IN_DISMISSED_KEY = 'wc.push.optin.dismissedAt';
/** Cookie twin of OPT_IN_DISMISSED_KEY - see the snooze section for why. */
const OPT_IN_DISMISSED_COOKIE = 'wc_push_optin_dismissedAt';
const OPT_IN_DONE_KEY = 'wc.push.optin.done';
const SYNC_MARKER_KEY = 'wc.push.sync';

/** The opt-in banner stays away this long after "not now". */
export const OPT_IN_SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;
/** Re-register an unchanged token at most this often (keeps lastSeenAt fresh
 *  on the server without a write on every app start). */
export const TOKEN_RESYNC_MS = 6 * 60 * 60 * 1000;
/** A freshly registered service worker gets this long to reach 'activated'
 *  (its install step fetches the Firebase SDK over the network) before enable
 *  reports a failure the user can simply retry. */
export const SW_ACTIVATE_TIMEOUT_MS = 10_000;
/** The most a sign-out will wait for this device to be unregistered. The hook
 *  runs inside Auth.signOut(), so a hang here would hang every logout. */
export const SIGN_OUT_CLEANUP_MS = 4_000;

// --- Types ------------------------------------------------------------------

export type PushState = 'unsupported' | 'ios-needs-install' | 'default' | 'granted' | 'denied';
export type PushPlatform = 'ios' | 'android' | 'desktop' | 'unknown';

export type PushFailureReason =
  | 'unsupported'
  | 'ios-needs-install'
  | 'denied'
  | 'dismissed'
  | 'not-enabled'
  | 'not-signed-in'
  | 'sw-failed'
  | 'token-failed'
  | 'register-failed';

export type PushResult = { ok: true } | { ok: false; reason: PushFailureReason; message: string };

/** Everything the state derivation needs, so tests can hand-build a device. */
export interface PushEnv {
  hasWindow: boolean;
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  hasNotification: boolean;
  permission: NotificationPermission | null;
  /** Running as an installed app (iOS navigator.standalone or display-mode). */
  standalone: boolean;
}

// --- Device / capability detection (pure) -----------------------------------

/** iPhone/iPod/iPad, including iPadOS which reports a desktop Mac UA. */
export function isIosDevice(env: Pick<PushEnv, 'userAgent' | 'platform' | 'maxTouchPoints'>): boolean {
  if (/iPad|iPhone|iPod/.test(env.userAgent || '')) return true;
  return env.platform === 'MacIntel' && env.maxTouchPoints > 1;
}

/** [major, minor] from an iOS UA ("CPU iPhone OS 16_4 like Mac OS X"), else
 *  null (iPadOS desktop-mode UAs carry no version). */
export function parseIosVersion(userAgent: string): [number, number] | null {
  const m = /\bOS (\d+)[_.](\d+)/.exec(userAgent || '');
  if (!m) return null;
  return [parseInt(m[1], 10), parseInt(m[2], 10)];
}

/** Web push exists on iOS only from 16.4, and only for an installed PWA. */
function iosSupportsWebPush(userAgent: string): boolean {
  const v = parseIosVersion(userAgent);
  if (!v) return true; // unknown version: assume modern, let the feature check decide
  return v[0] > 16 || (v[0] === 16 && v[1] >= 4);
}

/** iOS (new enough for web push) but not running from the Home Screen: push
 *  is impossible until the app is installed, so the UI must say so. */
export function isIosNotInstalledEnv(env: PushEnv): boolean {
  return (
    env.hasWindow &&
    isIosDevice(env) &&
    !env.standalone &&
    iosSupportsWebPush(env.userAgent)
  );
}

/** Can this browser do web push at all (service worker + Push API + Notification)? */
export function isPushSupportedEnv(env: PushEnv): boolean {
  return env.hasWindow && env.hasServiceWorker && env.hasPushManager && env.hasNotification;
}

/**
 * The single source of truth for what the UI should offer. The iOS-not-
 * installed check runs BEFORE the capability check on purpose: Safari in a tab
 * exposes neither PushManager nor Notification, which would otherwise read as a
 * dead-end "unsupported" instead of the actionable "add to Home Screen".
 */
export function derivePushState(env: PushEnv): PushState {
  if (!env.hasWindow) return 'unsupported';
  if (isIosNotInstalledEnv(env)) return 'ios-needs-install';
  if (!isPushSupportedEnv(env)) return 'unsupported';
  if (env.permission === 'granted') return 'granted';
  if (env.permission === 'denied') return 'denied';
  return 'default';
}

export function detectPlatform(env: Pick<PushEnv, 'userAgent' | 'platform' | 'maxTouchPoints'>): PushPlatform {
  if (isIosDevice(env)) return 'ios';
  const ua = env.userAgent || '';
  if (/Android/i.test(ua)) return 'android';
  if (/Windows|Macintosh|Mac OS X|X11|Linux|CrOS/i.test(ua)) return 'desktop';
  return 'unknown';
}

/** Snapshot the real browser. Every probe is guarded: this must not throw in
 *  SSR, jsdom, or a locked-down WebView. */
export function readPushEnv(): PushEnv {
  const hasWindow = typeof window !== 'undefined' && typeof navigator !== 'undefined';
  if (!hasWindow) {
    return {
      hasWindow: false, userAgent: '', platform: '', maxTouchPoints: 0,
      hasServiceWorker: false, hasPushManager: false, hasNotification: false,
      permission: null, standalone: false,
    };
  }
  const nav: any = navigator;
  let standalone = false;
  try {
    standalone =
      nav.standalone === true ||
      (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches);
  } catch { /* matchMedia can throw in odd WebViews */ }
  const hasNotification = typeof (window as any).Notification !== 'undefined';
  let permission: NotificationPermission | null = null;
  if (hasNotification) {
    try { permission = (window as any).Notification.permission; } catch { permission = null; }
  }
  return {
    hasWindow: true,
    userAgent: String(nav.userAgent || ''),
    platform: String(nav.platform || ''),
    maxTouchPoints: Number(nav.maxTouchPoints || 0),
    hasServiceWorker: 'serviceWorker' in navigator,
    hasPushManager: 'PushManager' in window,
    hasNotification,
    permission,
    standalone,
  };
}

export function isPushSupported(): boolean {
  return isPushSupportedEnv(readPushEnv());
}

export function isIosNotInstalled(): boolean {
  return isIosNotInstalledEnv(readPushEnv());
}

export function getPushState(): PushState {
  return derivePushState(readPushEnv());
}

// --- Opt-in banner snooze (pure + storage-injected) -------------------------

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** localStorage can be absent or throw (private mode, blocked site data). */
function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function readItem(storage: StorageLike | null, key: string): string | null {
  try { return storage ? storage.getItem(key) : null; } catch { return null; }
}

function writeItem(storage: StorageLike | null, key: string, value: string): void {
  try { if (storage) storage.setItem(key, value); } catch { /* best effort */ }
}

function removeItem(storage: StorageLike | null, key: string): void {
  try { if (storage) storage.removeItem(key); } catch { /* best effort */ }
}

/** True while a "not now" is still inside the snooze window. A timestamp far
 *  in the future (corrupt value / wild clock) is NOT treated as a snooze, or a
 *  single bad write would hide the banner forever. */
export function isSnoozeActive(dismissedAt: number, now: number, windowMs: number = OPT_IN_SNOOZE_MS): boolean {
  if (!Number.isFinite(dismissedAt) || dismissedAt <= 0) return false;
  return Math.abs(now - dismissedAt) < windowMs;
}

/**
 * The snooze also lives in a first-party cookie because several sign-out
 * handlers call localStorage.clear(), which would otherwise reset a "not now"
 * the next time the same person signs in. A cookie survives that wipe.
 * Secure: the app is HTTPS-only and this must never ride plain http.
 */
export interface CookieJar { read(): string; write(cookie: string): void }

/** document.cookie can be absent (SSR) or throw (sandboxed frame, blocked cookies). */
function defaultCookieJar(): CookieJar | null {
  if (typeof document === 'undefined') return null;
  return {
    read: () => document.cookie,
    write: (cookie) => { document.cookie = cookie; },
  };
}

function readCookie(jar: CookieJar | null, name: string): string | null {
  try {
    const all = jar ? jar.read() : '';
    for (const part of all.split(';')) {
      const eq = part.indexOf('=');
      if (eq > 0 && part.slice(0, eq).trim() === name) return part.slice(eq + 1).trim();
    }
  } catch { /* cookies blocked: fall back to storage alone */ }
  return null;
}

function writeCookie(jar: CookieJar | null, name: string, value: string, maxAgeMs: number): void {
  try {
    if (jar) jar.write(`${name}=${value}; Max-Age=${Math.floor(maxAgeMs / 1000)}; Path=/; SameSite=Lax; Secure`);
  } catch { /* best effort */ }
}

/** Snoozed if EITHER copy says so: storage is the primary, the cookie covers
 *  the case where storage was wiped. */
export function isOptInDismissed(
  storage: StorageLike | null = defaultStorage(),
  now: number = Date.now(),
  cookies: CookieJar | null = defaultCookieJar(),
): boolean {
  const raw = readItem(storage, OPT_IN_DISMISSED_KEY);
  if (raw && isSnoozeActive(Number(raw), now)) return true;
  const fromCookie = readCookie(cookies, OPT_IN_DISMISSED_COOKIE);
  return !!fromCookie && isSnoozeActive(Number(fromCookie), now);
}

export function recordOptInDismissal(
  storage: StorageLike | null = defaultStorage(),
  now: number = Date.now(),
  cookies: CookieJar | null = defaultCookieJar(),
): void {
  writeItem(storage, OPT_IN_DISMISSED_KEY, String(now));
  writeCookie(cookies, OPT_IN_DISMISSED_COOKIE, String(now), OPT_IN_SNOOZE_MS);
}

/** Set once push has been enabled: the banner never shows again. */
export function markOptInDone(storage: StorageLike | null = defaultStorage()): void {
  writeItem(storage, OPT_IN_DONE_KEY, '1');
}

export function isOptInDone(storage: StorageLike | null = defaultStorage()): boolean {
  return readItem(storage, OPT_IN_DONE_KEY) === '1';
}

/** Should the banner be offered right now? */
export function shouldShowOptIn(
  state: PushState,
  storage: StorageLike | null = defaultStorage(),
  now: number = Date.now(),
  cookies: CookieJar | null = defaultCookieJar(),
): boolean {
  if (state !== 'default' && state !== 'ios-needs-install') return false;
  if (isOptInDone(storage)) return false;
  return !isOptInDismissed(storage, now, cookies);
}

// --- Deep link ids (pure) ---------------------------------------------------

/**
 * Email doc ids are 'g_' + 32 hex (Graph sync) or a 20-char auto id (legacy).
 * The allowlist is deliberately broader than that but excludes everything a
 * Firestore path could be abused with: '/', '.', whitespace, control chars.
 * Reserved '__name__' ids are rejected too.
 */
export function isValidEmailDocId(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(value)) return false;
  if (value.startsWith('__') && value.endsWith('__')) return false;
  return true;
}

/** '?openEmail=<id>' -> id, or null when absent / malformed. */
export function parseOpenEmailId(search: string | null | undefined): string | null {
  if (!search) return null;
  let value: string | null = null;
  try {
    value = new URLSearchParams(search).get(OPEN_EMAIL_PARAM);
  } catch {
    return null;
  }
  return isValidEmailDocId(value) ? value : null;
}

/** The service worker's { type: 'OPEN_EMAIL', emailId } postMessage. */
export function parseOpenEmailMessage(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as { type?: unknown; emailId?: unknown };
  if (d.type !== 'OPEN_EMAIL') return null;
  return isValidEmailDocId(d.emailId) ? d.emailId : null;
}

/** path + search + hash of `href` with the openEmail param removed. Only the
 *  relative part is returned so replaceState can never change origin. */
export function stripOpenEmailParam(href: string): string {
  try {
    const u = new URL(href, 'http://localhost');
    u.searchParams.delete(OPEN_EMAIL_PARAM);
    return u.pathname + u.search + u.hash;
  } catch {
    return '/';
  }
}

// --- Token re-registration throttle (pure) ----------------------------------

export interface SyncMarker { h: string; t: number; uid: string }

/** Small non-cryptographic hash: enough to notice a changed token without
 *  keeping the token itself in localStorage. */
export function hashToken(token: string): string {
  let h = 5381;
  for (let i = 0; i < token.length; i++) h = ((h * 33) ^ token.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

export function shouldResyncToken(
  prev: SyncMarker | null,
  token: string,
  uid: string,
  now: number,
  resyncMs: number = TOKEN_RESYNC_MS,
): boolean {
  if (!prev) return true;
  if (prev.uid !== uid) return true; // a different person signed in on this device
  if (prev.h !== hashToken(token)) return true; // FCM rotated the token
  if (!Number.isFinite(prev.t) || now - prev.t < 0) return true; // corrupt / clock went back
  return now - prev.t >= resyncMs;
}

function readSyncMarker(storage: StorageLike | null): SyncMarker | null {
  const raw = readItem(storage, SYNC_MARKER_KEY);
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    if (v && typeof v.h === 'string' && typeof v.t === 'number' && typeof v.uid === 'string') return v;
  } catch { /* fall through */ }
  return null;
}

// --- Impure entry points ----------------------------------------------------

const fail = (reason: PushFailureReason, message: string): PushResult => ({ ok: false, reason, message });

function shortMessage(e: unknown): string {
  const m = (e as any)?.message;
  return (typeof m === 'string' && m.trim() ? m.trim() : 'unknown error').slice(0, 140);
}

/** Firebase-style or HttpError code, for logging (never a token). */
function errCode(e: unknown): string {
  const c = (e as any)?.code ?? (e as any)?.status ?? (e as any)?.name;
  return typeof c === 'string' || typeof c === 'number' ? String(c) : 'error';
}

function warn(where: string, reason: string, e?: unknown): void {
  // eslint-disable-next-line no-console
  console.warn(`[push] ${where}: ${reason}${e === undefined ? '' : ` (${errCode(e)})`}`);
}

/** Wait for Firebase Auth to finish restoring the session, then return the uid. */
async function currentUid(): Promise<string | null> {
  try { await auth.authStateReady(); } catch { /* fall through to currentUser */ }
  return auth.currentUser?.uid ?? null;
}

/** Notification.requestPermission with the legacy callback form folded in
 *  (Safari < 14 returns undefined instead of a promise). Must be called
 *  synchronously from the click handler - iOS/Safari reject it otherwise. */
function requestPermission(): Promise<NotificationPermission> {
  return new Promise((resolve) => {
    try {
      const N: any = (window as any).Notification;
      const maybe = N.requestPermission((p: NotificationPermission) => resolve(p));
      if (maybe && typeof maybe.then === 'function') {
        maybe.then(resolve, () => resolve('default'));
      }
    } catch {
      resolve('default');
    }
  });
}

/**
 * register() resolves while a first-ever worker is still 'installing', and the
 * Push API's subscribe() (which getToken calls) rejects with "no active Service
 * Worker" until one is active. So a brand-new device's first enable failed
 * right after the user accepted the permission prompt, and only the second tap
 * worked. Wait for activation here instead of pushing that onto the user.
 *
 * Returns at once when the registration already has an activated worker (every
 * app start after the first) and when it exposes no worker at all - there is
 * nothing to wait on, so let getToken report whatever is really wrong.
 */
export function waitForActiveWorker(
  registration: ServiceWorkerRegistration,
  timeoutMs: number = SW_ACTIVATE_TIMEOUT_MS,
): Promise<void> {
  if (registration.active && registration.active.state === 'activated') return Promise.resolve();
  const worker = registration.installing || registration.waiting || registration.active;
  if (!worker) return Promise.resolve();

  return new Promise<void>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (err?: Error) => {
      if (timer !== undefined) clearTimeout(timer);
      worker.removeEventListener('statechange', onChange);
      if (err) reject(err); else resolve();
    };
    const onChange = () => {
      if (worker.state === 'activated') finish();
      // A worker whose install failed (script fetch blocked, syntax error) goes
      // redundant and will never activate - fail now rather than after the timeout.
      else if (worker.state === 'redundant') finish(new Error('service worker install failed'));
    };
    worker.addEventListener('statechange', onChange);
    timer = setTimeout(() => finish(new Error('service worker activation timed out')), timeoutMs);
    onChange(); // the state may have advanced between the checks above and the listener
  });
}

type TokenResult = { ok: true; token: string } | { ok: false; reason: PushFailureReason; message: string };

/** Register the SW and mint/refresh the FCM token. Caller has ALREADY made sure
 *  permission is 'granted' - getToken would otherwise prompt by itself. */
async function acquireToken(): Promise<TokenResult> {
  let sdk: typeof import('firebase/messaging');
  try {
    // Dynamic: keeps the messaging SDK out of the main bundle for users who
    // never enable push.
    sdk = await import('firebase/messaging');
    if (!(await sdk.isSupported())) {
      return { ok: false, reason: 'unsupported', message: "this browser can't receive push notifications" };
    }
  } catch (e) {
    warn('acquireToken', 'sdk-load-failed', e);
    return { ok: false, reason: 'unsupported', message: "this browser can't receive push notifications" };
  }

  let registration: ServiceWorkerRegistration;
  try {
    registration = await navigator.serviceWorker.register(PUSH_SW_URL);
  } catch (e) {
    warn('acquireToken', 'sw-register-failed', e);
    return { ok: false, reason: 'sw-failed', message: "couldn't start the notification service worker" };
  }

  try {
    await waitForActiveWorker(registration);
  } catch (e) {
    warn('acquireToken', 'sw-activate-failed', e);
    return { ok: false, reason: 'sw-failed', message: "the notification service worker didn't start - try again" };
  }

  try {
    const token = await sdk.getToken(sdk.getMessaging(app), {
      serviceWorkerRegistration: registration,
      ...(VAPID_KEY ? { vapidKey: VAPID_KEY } : {}),
    });
    if (!token) return { ok: false, reason: 'token-failed', message: 'no notification token was issued' };
    return { ok: true, token };
  } catch (e) {
    warn('acquireToken', 'get-token-failed', e);
    return { ok: false, reason: 'token-failed', message: `couldn't get a notification token: ${shortMessage(e)}` };
  }
}

/** POST the token to our own endpoint and remember we did (throttle marker). */
async function registerTokenWithServer(token: string, uid: string): Promise<PushResult> {
  const env = readPushEnv();
  try {
    await authPostConfirmed(REGISTER_PUSH_TOKEN_URL, {
      token,
      userAgent: env.userAgent.slice(0, 200),
      platform: detectPlatform(env),
    });
  } catch (e) {
    warn('register', 'server-rejected', e);
    return fail('register-failed', `couldn't save this device for notifications: ${shortMessage(e)}`);
  }
  const marker: SyncMarker = { h: hashToken(token), t: Date.now(), uid };
  writeItem(defaultStorage(), SYNC_MARKER_KEY, JSON.stringify(marker));
  return { ok: true };
}

let enableInFlight: Promise<PushResult> | null = null;

async function doEnable(): Promise<PushResult> {
  const state = getPushState();
  if (state === 'unsupported') {
    return fail('unsupported', "this browser can't receive push notifications");
  }
  if (state === 'ios-needs-install') {
    return fail('ios-needs-install', 'add this app to your Home Screen first, then enable notifications from there');
  }
  if (state === 'denied') {
    return fail('denied', 'notifications are blocked for this site - allow them in your browser settings, then try again');
  }

  // The permission prompt MUST be the first async step (no awaited import or
  // fetch before it) so it still counts as inside the user's tap.
  if (state === 'default') {
    const permission = await requestPermission();
    if (permission === 'denied') {
      return fail('denied', 'notifications are blocked for this site - allow them in your browser settings, then try again');
    }
    if (permission !== 'granted') {
      return fail('dismissed', 'notifications were not allowed');
    }
  }

  const uid = await currentUid();
  if (!uid) return fail('not-signed-in', 'sign in to turn on notifications');

  ensureSignOutCleanup();
  const token = await acquireToken();
  if (!token.ok) return fail(token.reason, token.message);

  return registerTokenWithServer(token.token, uid);
}

/**
 * Ask for permission, mint an FCM token and register it for the signed-in user.
 * MUST be called directly from a user gesture (a click/tap handler). Never throws.
 */
export function enablePush(): Promise<PushResult> {
  // One prompt at a time: a double-tap must not start two permission flows.
  if (enableInFlight) return enableInFlight;
  enableInFlight = doEnable()
    .catch((e): PushResult => {
      warn('enable', 'unexpected', e);
      return fail('token-failed', `couldn't turn on notifications: ${shortMessage(e)}`);
    })
    .finally(() => { enableInFlight = null; });
  return enableInFlight;
}

/** Unregister this device's token (server + FCM). The browser permission itself
 *  cannot be revoked from script; the user does that in site settings. Never throws. */
export async function disablePush(): Promise<PushResult> {
  try {
    const state = getPushState();
    if (state === 'unsupported' || state === 'ios-needs-install') {
      return fail(state, "this browser can't receive push notifications");
    }
    // Only a granted permission can have a token; asking getToken otherwise
    // would pop a permission prompt from inside a "turn off" action.
    if (state !== 'granted') {
      removeItem(defaultStorage(), SYNC_MARKER_KEY);
      return { ok: true };
    }

    const registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration) {
      removeItem(defaultStorage(), SYNC_MARKER_KEY);
      return { ok: true };
    }

    const sdk = await import('firebase/messaging');
    if (!(await sdk.isSupported())) return fail('unsupported', "this browser can't receive push notifications");
    const messaging = sdk.getMessaging(app);

    let token: string | null = null;
    try {
      token = await sdk.getToken(messaging, {
        serviceWorkerRegistration: registration,
        ...(VAPID_KEY ? { vapidKey: VAPID_KEY } : {}),
      });
    } catch (e) {
      warn('disable', 'get-token-failed', e);
    }

    if (token && (await currentUid())) {
      try {
        await authPostConfirmed(UNREGISTER_PUSH_TOKEN_URL, { token });
      } catch (e) {
        // Not fatal: the server prunes a dead token on its first failed send.
        warn('disable', 'server-unregister-failed', e);
      }
    }

    await sdk.deleteToken(messaging);
    removeItem(defaultStorage(), SYNC_MARKER_KEY);
    return { ok: true };
  } catch (e) {
    warn('disable', 'unexpected', e);
    return fail('token-failed', `couldn't turn off notifications: ${shortMessage(e)}`);
  }
}

// --- Sign-out cleanup ---------------------------------------------------------

/** Resolve when `work` settles OR after `ms`, whichever is first. Never rejects:
 *  the caller is a sign-out that must proceed no matter what. */
function settleWithin(work: Promise<unknown>, ms: number): Promise<void> {
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    work.then(() => undefined, () => undefined).then(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}

/**
 * Firebase Auth beforeAuthStateChanged callback: when the session is about to be
 * dropped, unregister this device first.
 *
 * It has to happen HERE. Once signOut() completes there is no ID token, so the
 * unregister endpoint can no longer be called, and the token would stay in the
 * signed-out user's push_tokens doc - a phone handed on (or retired) would keep
 * lighting up with that user's client emails. The middleware runs while
 * auth.currentUser is still the outgoing user, so disablePush's authenticated
 * POST still works; it also deletes the FCM token so the next sign-in mints a
 * fresh one (syncPushTokenIfGranted re-registers it for whoever signs in).
 *
 * Only a real sign-out (next === null) is handled. A sign-in or user switch
 * arrives with a user; the server already moves a token to whoever registers it.
 *
 * Must never throw and never hang: a throw aborts the sign-out with
 * auth/login-blocked and a hang stalls it, so errors are swallowed and the wait
 * is capped at SIGN_OUT_CLEANUP_MS. If the cap hits, the unregister request has
 * normally already been sent (the token lookup is local) and completes on its own.
 */
export async function unregisterDeviceBeforeSignOut(next: User | null): Promise<void> {
  try {
    if (next !== null) return;
    if (!auth.currentUser) return;
    await settleWithin(disablePush(), SIGN_OUT_CLEANUP_MS);
  } catch (e) {
    warn('signout', 'unexpected', e);
  }
}

let signOutHookInstalled = false;

/** Install the sign-out hook once. Called from the two places a token can come
 *  into existence (enable, and the app-start sync), so the hook exists exactly
 *  when there may be something to clean up - and covers every sign-out call site
 *  (several components plus passkeys.ts and firebase.ts) without touching them. */
function ensureSignOutCleanup(): void {
  if (signOutHookInstalled) return;
  try {
    if (typeof auth.beforeAuthStateChanged !== 'function') return;
    auth.beforeAuthStateChanged(unregisterDeviceBeforeSignOut);
    signOutHookInstalled = true;
  } catch (e) {
    warn('signout', 'hook-install-failed', e);
  }
}

/**
 * App start, permission already granted: refresh the token and re-register it
 * if it changed, the signed-in user changed, or it has not been confirmed to
 * the server for a while. Silent by design: a failure here must never reach the
 * UI (the user did nothing), and the next app start simply tries again.
 */
export async function syncPushTokenIfGranted(): Promise<PushResult> {
  try {
    if (getPushState() !== 'granted') return fail('not-enabled', 'notifications are not enabled');

    const uid = await currentUid();
    if (!uid) return fail('not-signed-in', 'not signed in');

    ensureSignOutCleanup();
    const token = await acquireToken();
    if (!token.ok) return fail(token.reason, token.message);

    const prev = readSyncMarker(defaultStorage());
    if (!shouldResyncToken(prev, token.token, uid, Date.now())) return { ok: true };

    return await registerTokenWithServer(token.token, uid);
  } catch (e) {
    warn('sync', 'unexpected', e);
    return fail('token-failed', shortMessage(e));
  }
}
